import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { MAPPED, UNMAPPED } from "src/main/Accounting/constants"

import AlertBanner from "src/components/AlertBanner"
import Badge from "src/components/Badge"
import { statusToken } from "src/components/Badge/status_tokens"
import Button from "src/components/Button"
import Chips from "src/components/Chips"
import DataTable from "src/components/DataTable"
import ActionConfirmModal from "src/components/DataTableV2/ActionConfirmModal"
import Form from "src/components/Form"
import Loader from "src/components/Loader"
import OverflowMenu from "src/components/OverflowMenu"
import Tooltip from "src/components/Tooltip"

import { queryInvoiceItems, updateInvoiceItem } from "src/api/Apideck"
import { queryMappableDockwaProducts } from "src/api/Integrations"

import useDebounce from "src/hooks/use_debounce"
import { useToast } from "src/hooks/use_toast"
import useWindowSize from "src/hooks/use_window_size"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { snakecaseToCamelCase, titlecase } from "src/utils/string_helpers"
import { updateUrlQueryParams } from "src/utils/url/parsing/marina"

import { AccountingContext } from "./AccountingView"
import VirtualizedInvoiceItemList from "./VirtualizedInvoiceItemList"
import useAccountsAndItemsImport from "./useAccountsAndItemsImport"

const EMPTY_NO_FILTERS =
  "No products match the selected filters, please try adjusting your selection(s)."
const EMPTY_WITH_FILTERS = "No products available to be mapped at this time."
const PAGE_SIZE = 25
const DEFAULT_SORT_KEY = "product"

const parseFailedSyncs = (value) => {
  if (value === "true") return true
  if (value === "false") return false
  return undefined
}

const writeUrlParams = (params) => {
  updateUrlQueryParams({
    categories: params.categories.join(","),
    failed_syncs:
      params.failedSyncs === undefined ? "" : String(params.failedSyncs),
    search_term: params.searchTerm,
    sort_key: params.sortKey,
    sort_direction: params.sortDirection,
    status: params.status,
    page: params.page,
  })
}

const ItemMappingTab = () => {
  const {
    categoryOptions,
    integration,
    marinaSlug,
    selectedAccountingService,
  } = useContext(AccountingContext)
  const queryClient = useQueryClient()
  const showToast = useToast()
  const urlParams = new URLSearchParams(window.location.search)
  const [queryableInvoiceItems, setQueryableInvoiceItems] = useState([])
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [filters, setFilters] = useState({
    categories:
      urlParams.getAll("categories").length > 0
        ? urlParams.getAll("categories")[0].split(",")
        : [],
    // undefined until the first response: the server picks the default.
    failedSyncs: parseFailedSyncs(urlParams.get("failed_syncs")),
    page: Number(urlParams.get("page")) || 1,
    searchTerm: urlParams.get("search_term") || "",
    sortDirection: urlParams.get("sort_direction") || "asc",
    sortKey: urlParams.get("sort_key")
      ? snakecaseToCamelCase(urlParams.get("sort_key"))
      : DEFAULT_SORT_KEY,
    status: urlParams.get("status") || "",
  })
  const {
    categories,
    failedSyncs,
    page,
    searchTerm,
    sortDirection,
    sortKey,
    status,
  } = filters
  const hasFiltersApplied =
    categories.length > 0 || !!searchTerm || !!status || failedSyncs === true
  // The filters as the user last set them, including changes still waiting on the debounce. Every change builds on
  // this, so a later change never drops an earlier one that has not reached state yet.
  const latestFilters = useRef(filters)
  const applyLatestFilters = useCallback(
    () => setFilters(latestFilters.current),
    []
  )
  const [debouncedApplyFilters] = useDebounce(applyLatestFilters)
  const { isLargeScreen } = useWindowSize()

  const updateParams = useCallback(
    (changes, { debounce = true } = {}) => {
      const nextFilters = { ...latestFilters.current, ...changes }
      latestFilters.current = nextFilters
      writeUrlParams(nextFilters)
      if (debounce) {
        debouncedApplyFilters()
      } else {
        setFilters(nextFilters)
      }
    },
    [debouncedApplyFilters]
  )

  const handleCategoryFilterChange = (event) => {
    const { id, checked } = event.target
    const currentCategories = latestFilters.current.categories
    const updatedCategories = checked
      ? [...currentCategories, id]
      : currentCategories.filter((category) => category !== id)
    setValue(id, checked)
    updateParams({ categories: updatedCategories, page: 1 })
  }

  const handleColumnSort = (key) => {
    const { sortKey: currentSortKey, sortDirection: currentSortDirection } =
      latestFilters.current
    let newSortDirection = null
    let newSortKey = key
    if (currentSortKey === key) {
      if (currentSortDirection === "asc") {
        newSortDirection = "desc"
      } else {
        newSortKey = null
      }
    } else {
      newSortDirection = "asc"
    }

    updateParams({
      sortKey: newSortKey,
      sortDirection: newSortDirection,
      page: 1,
    })
  }

  const handleFailedSyncsToggle = () => {
    updateParams(
      { failedSyncs: !latestFilters.current.failedSyncs, page: 1 },
      { debounce: false }
    )
  }

  const handleFilterInvoiceItems = (event) => {
    const { value } = event.target
    setValue("filterQueryableInvoiceItems", value)
    setQueryableInvoiceItems(
      invoiceItems.filter((invoiceItem) =>
        invoiceItem.name.toLowerCase().includes(value.toLowerCase())
      )
    )
  }

  const handlePageChange = (page) => {
    updateParams({ page: page.selected })
  }

  const handleResetFilters = () => {
    categoryOptions.forEach(([categoryId, categoryLabel]) => {
      setValue(categoryId, false)
    })
    updateParams({
      categories: [],
      failedSyncs: false,
      searchTerm: "",
      status: "",
      page: 1,
    })
  }

  const handleSearch = (event) => {
    setValue("searchTerm", event.target.value)
    updateParams({ searchTerm: event.target.value, page: 1 })
  }

  const handleStatusFilterChange = (event) => {
    const { name, checked } = event.target

    setValue("mapped", name === MAPPED ? checked : false)
    setValue("unmapped", name === UNMAPPED ? checked : false)

    setStatusMenuOpen(false)
    const statusValue = checked ? name : ""
    updateParams({ status: statusValue, page: 1 })
  }

  const resetQueryableInvoiceItems = () => {
    setValue("filterQueryableInvoiceItems", "")
    setQueryableInvoiceItems(invoiceItems)
  }

  const { register, setValue, watch } = useForm({
    defaultValues: {
      mapped: filters.status === MAPPED,
      filterQueryableInvoiceItems: "",
      searchTerm,
      unmapped: filters.status === UNMAPPED,
      ...Object.fromEntries(
        categoryOptions.map(([categoryId, categoryLabel]) => [
          categoryId,
          categories.includes(categoryId),
        ])
      ),
    },
  })

  const COLDEFS = [
    {
      key: "status",
      header: "Status",
      render: (data) => (
        <Badge
          color={statusToken(data.apideckInvoiceItem ? MAPPED : UNMAPPED)}
          text={
            data.apideckInvoiceItem ? titlecase(MAPPED) : titlecase(UNMAPPED)
          }
        />
      ),
      sortable: true,
      width: "10%",
    },
    {
      key: "product",
      header: "Dockwa Item",
      render: (data) => data.name,
      sortable: true,
      width: "20%",
    },
    {
      key: "category",
      header: "Category",
      render: (data) => data.productCategoryName,
      sortable: true,
      width: "15%",
    },
    {
      key: "price",
      header: "Price",
      render: (data) =>
        data.pricePerUnit
          ? formatCurrencyFromMinorUnits({
              amount: data.pricePerUnit,
              precision: data.pricePrecision === "cents" ? 2 : 4,
            })
          : null,
      sortable: true,
      width: "15%",
    },
    {
      key: "syncErrors",
      header: "Sync Errors",
      render: (data) =>
        data.syncError ? (
          <Tooltip
            text={data.syncError.message}
            placement="top"
            maxWidth="320px"
            tabIndex={0}
          >
            <span className="flex items-center gap-1 font-semibold text-red-700">
              <i className="fa-solid fa-circle-exclamation" />
              {data.syncError.label}
            </span>
          </Tooltip>
        ) : null,
      sortable: false,
      width: "20%",
    },
    {
      key: "product",
      header: "Accounting Item",
      render: (data) => (
        <OverflowMenu
          menuButtonVariant="quarternary"
          menuButtonLabel={data.apideckInvoiceItem?.name || "Select"}
          hasErrors={!!data.syncError}
          menuItemWidth="450px"
          menuButtonFullWidth
        >
          <div className="p-2">
            <Form.IconTextField
              id="filter-invoice-items"
              icon={
                <i className="fa-solid fa-magnifying-glass text-gray-600" />
              }
              position="left"
              value={watch("filterQueryableInvoiceItems")}
              isClearable
              placeholder="Search items"
              onBlur={resetQueryableInvoiceItems}
              onChange={handleFilterInvoiceItems}
              onClearSelection={resetQueryableInvoiceItems}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <div data-testid={`product-${data.id}-mapping-select`}>
            <VirtualizedInvoiceItemList
              queryableInvoiceItems={queryableInvoiceItems}
              currentProduct={data}
              apideckInvoiceItem={data.apideckInvoiceItem}
              isMutatingInvoiceItem={isMutatingInvoiceItem}
              mutateInvoiceItem={mutateInvoiceItem}
            />
          </div>
        </OverflowMenu>
      ),
      sortable: false,
      width: "20%",
    },
  ]

  const sortConfig = {
    sortKey: sortKey ? snakecaseToCamelCase(sortKey) : null,
    sortDirection: sortDirection || null,
    onColumnSort: handleColumnSort,
  }

  const { isLoading: isLoadingInvoiceItems, data: invoiceItems } = useQuery({
    queryKey: ["invoice_items", marinaSlug],
    queryFn: () => {
      return queryInvoiceItems(marinaSlug)
    },
    onSuccess: (data) => setQueryableInvoiceItems(data),
    refetchOnWindowFocus: false,
    enabled: !!integration?.setupComplete,
    retry: false,
  })

  const [failedSyncsOverPageCap, setFailedSyncsOverPageCap] = useState(false)
  // The server returns the whole failed-syncs set on one page, so the browser pages it and a page change
  // sends no request. Above the server's cap it pages on the server instead.
  const browserPaginated = failedSyncs === true && !failedSyncsOverPageCap
  const serverParams = {
    categories,
    failedSyncs,
    page: browserPaginated ? 1 : page,
    searchTerm,
    sortDirection,
    sortKey,
    status,
  }
  const productsQueryKey = ["products", marinaSlug, serverParams]

  const { isLoading: isLoadingDockwaProducts, data: dockwaProducts } = useQuery(
    {
      queryKey: productsQueryKey,
      queryFn: () =>
        queryMappableDockwaProducts({
          marinaSlug,
          filters: serverParams,
          pageSize: PAGE_SIZE,
        }),
      onSuccess: (data) => {
        const { failed_syncs_applied: applied, page_count: pageCount } = data
        if (applied) setFailedSyncsOverPageCap(pageCount > 1)
        if (failedSyncs !== undefined) return

        // Adopt the filter state the server chose, under a key that already holds this response, so
        // switching to it fetches nothing.
        queryClient.setQueryData(
          [
            "products",
            marinaSlug,
            {
              ...serverParams,
              failedSyncs: applied,
              page: applied && pageCount === 1 ? 1 : page,
            },
          ],
          data
        )
        latestFilters.current = {
          ...latestFilters.current,
          failedSyncs: applied,
        }
        setFilters((current) => ({ ...current, failedSyncs: applied }))
      },
      keepPreviousData: true,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: !!integration?.setupComplete,
      retry: false,
    }
  )

  const items = dockwaProducts?.items || []
  // Read from the rendered data, which can still be the previous key's while a new key loads.
  const showsWholeSet =
    !!dockwaProducts?.failed_syncs_applied && dockwaProducts.page_count === 1
  const browserPageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
  const syncErrorCount = dockwaProducts?.sync_error_count || 0

  useEffect(() => {
    if (!showsWholeSet || page <= browserPageCount) return

    updateParams({ page: browserPageCount }, { debounce: false })
  }, [showsWholeSet, browserPageCount, page, updateParams])

  const patchProducts = ({ products, sync_error_count: syncErrorCount }) => {
    const updatedProducts = new Map(
      products.map((product) => [product.id, product])
    )
    queryClient.setQueryData(
      productsQueryKey,
      (current) =>
        current && {
          ...current,
          items: current.items.map(
            (item) => updatedProducts.get(item.id) || item
          ),
          sync_error_count: syncErrorCount,
        }
    )
  }

  const { mutate: mutateInvoiceItem, isLoading: isMutatingInvoiceItem } =
    useMutation(({ id, data }) => updateInvoiceItem({ marinaSlug, id, data }), {
      onSuccess: (data) => {
        resetQueryableInvoiceItems()
        queryClient.invalidateQueries("invoice_items")
        if (showsWholeSet) {
          // The whole set is in the browser, so rows stay where they are: mark other product queries stale,
          // then patch the visible one in place.
          queryClient.invalidateQueries("products", { refetchActive: false })
          patchProducts(data)
        } else {
          // A page the server offsets must refetch, or rows a save moved out of its filter shift the next page.
          queryClient.invalidateQueries("products")
        }
        showToast("Item mapping updated successfully!", {
          type: "success",
          duration: 3,
        })
      },
      onError: (error) => {
        showToast(error.toString(), { type: "error", duration: 5 })
      },
    })

  const { isImportingCodes, manualCodesConfirmation, onImportClick } =
    useAccountsAndItemsImport({ source: "item_mapping" })

  const renderCategoryFilter = () => (
    <OverflowMenu
      menuButtonLabel="Category"
      menuButtonFullWidth={!isLargeScreen}
    >
      <div className="space-y-2 p-4">
        {categoryOptions.map(([categoryId, categoryLabel]) => {
          return (
            <Form.Checkbox
              {...register(categoryId)}
              key={categoryId}
              label={categoryLabel}
              onChange={handleCategoryFilterChange}
            />
          )
        })}
      </div>
    </OverflowMenu>
  )

  const renderFailedSyncsCheckbox = () => (
    <div className="mx-3 flex items-center">
      <Form.Checkbox
        name="failedSyncs"
        label="Only show items with failed syncs"
        labelClassName="whitespace-nowrap"
        checked={!!failedSyncs}
        onChange={handleFailedSyncsToggle}
        compact
      />
    </div>
  )

  const renderFilterChips = () => {
    return (
      <div className="flex flex-wrap">
        {searchTerm && (
          <Chips.Chip
            label="Search"
            text={`Search: ${searchTerm}`}
            onClick={() => {
              setValue("searchTerm", "")
              updateParams({ searchTerm: "", page: 1 })
            }}
          />
        )}
        {categories.map((appliedCategoryFilterId) => {
          const [appliedCategoryId, appliedCategoryLabel] =
            categoryOptions.find(
              ([categoryId, categoryLabel]) =>
                categoryId === appliedCategoryFilterId
            )
          return (
            <Chips.Chip
              key={`category-chip-${appliedCategoryLabel}`}
              label="Category"
              text={`Category: ${appliedCategoryLabel}`}
              onClick={() => {
                setValue(appliedCategoryId, false)
                updateParams({
                  categories: latestFilters.current.categories.filter(
                    (categoryFilterId) => categoryFilterId !== appliedCategoryId
                  ),
                  page: 1,
                })
              }}
            />
          )
        })}
        {!!status && (
          <Chips.Chip
            key={`status-chip-${status}`}
            label="Status"
            text={`Status: ${titlecase(status)}`}
            onClick={() => {
              setValue(status, false)
              updateParams({ status: "", page: 1 })
            }}
          />
        )}
        {hasFiltersApplied && (
          <div className="flex w-min items-center">
            <Button variant="ghost" small onClick={handleResetFilters}>
              Reset
            </Button>
          </div>
        )}
      </div>
    )
  }

  const renderSearchInput = () => (
    <Form.IconTextField
      {...register("searchTerm")}
      icon={
        <i className="fa-solid fa-magnifying-glass text-xl text-gray-400" />
      }
      id="invoice-item-search"
      isClearable
      onChange={handleSearch}
      onClearSelection={() => {
        setValue("searchTerm", "")
        updateParams({ searchTerm: "" })
      }}
      position="left"
      value={watch("searchTerm")}
      placeholder="Search item name"
    />
  )

  const renderStatusFilter = () => (
    <OverflowMenu
      menuButtonLabel="Status"
      manualClose={true}
      menuOpen={statusMenuOpen}
      setMenuOpen={setStatusMenuOpen}
      menuButtonFullWidth={!isLargeScreen}
    >
      <div className="space-y-2 p-4">
        <Form.Checkbox
          {...register(MAPPED)}
          key={MAPPED}
          label={titlecase(MAPPED)}
          onChange={handleStatusFilterChange}
        />
        <Form.Checkbox
          {...register(UNMAPPED)}
          key={UNMAPPED}
          label={titlecase(UNMAPPED)}
          onChange={handleStatusFilterChange}
        />
      </div>
    </OverflowMenu>
  )

  const renderSyncErrorBanner = () => {
    if (syncErrorCount === 0) return null

    const subject =
      syncErrorCount === 1 ? "1 item has" : `${syncErrorCount} items have`
    const provider = selectedAccountingService?.name || "your accounting system"

    return (
      <div className="mb-6 inline-block">
        <AlertBanner type="error">
          {`${subject} had failed syncs. Invoices containing these items can't sync to ${provider} currently.`}
        </AlertBanner>
      </div>
    )
  }

  const renderTable = () => {
    if (isLoadingInvoiceItems || isLoadingDockwaProducts) {
      return (
        <div className="bg-white">
          <Loader name="item mappings" />
        </div>
      )
    } else if (items.length === 0) {
      return (
        <div className="my-5 text-center">
          {hasFiltersApplied ? EMPTY_NO_FILTERS : EMPTY_WITH_FILTERS}
        </div>
      )
    } else {
      return (
        <DataTable
          name="invoice-items"
          isLoading={isLoadingInvoiceItems || isLoadingDockwaProducts}
          colDefs={COLDEFS}
          rowData={
            showsWholeSet
              ? items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
              : items
          }
          page={showsWholeSet ? page : dockwaProducts?.page}
          numberOfPages={
            showsWholeSet ? browserPageCount : dockwaProducts?.page_count
          }
          onPageChange={handlePageChange}
          sortConfig={sortConfig}
          fullHeight
          hideActions
          hideSettings
          pagination
        />
      )
    }
  }

  return (
    <div className="mb-[250px]">
      <h4 className="mb-1 pt-6 font-semibold text-gray-900">Item Mapping</h4>
      <div className="mb-8">
        <span className="text-gray-800">
          Map your {selectedAccountingService?.name || "Accounting System"}{" "}
          items to Dockwa products.
        </span>
      </div>
      {renderSyncErrorBanner()}
      <div className="flex flex-wrap items-center lg:flex-nowrap">
        <div className="w-full pb-2 lg:mr-2 lg:w-[336px] lg:p-0">
          {renderSearchInput()}
        </div>
        <div className="flex w-full lg:w-min">
          <div className="flex w-full flex-wrap space-y-2 sm:flex-nowrap sm:space-y-0">
            {renderCategoryFilter()}
            {renderStatusFilter()}
            {renderFailedSyncsCheckbox()}
          </div>
        </div>
        <div className="ml-auto pt-2 lg:pt-0">
          <Button
            onClick={onImportClick}
            disabled={isImportingCodes}
            isLoading={isImportingCodes}
            variant="secondary"
          >
            {isImportingCodes ? "Importing..." : "Re-sync Items"}
          </Button>
        </div>
      </div>
      <div className="px-4 xl:px-0">
        <Chips.List>{renderFilterChips()}</Chips.List>
      </div>
      <div className="my-8">{renderTable()}</div>
      <ActionConfirmModal {...manualCodesConfirmation} />
    </div>
  )
}

export default ItemMappingTab
